<template>
	<div>
		<div class="row gutter-sm q-mb-lg">
			<div class="col-xs-12 col-sm-12">
				<q-field :count="256">
					<q-input
						v-model="title"
						ref="title"
						:float-label="$t('title')"
						:error="$v.title.$error"
						@input="$v.title.$touch"
						:disable="!manageLiveClasses"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-field :count="1024">
					<q-input
						v-model="description"
						ref="description"
						:float-label="$t('introduction')"
						rows="1"
						:max-height="100"
						type="textarea"
						:error="$v.description.$error"
						@input="$v.description.$touch"
						:disable="!manageLiveClasses"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="platform_type"
					:options="getLiveClassPlatformTypes"
					:float-label="
						platform_type === 'jitsi'
							? $t('stream_type_jitsi_8x8_warning')
							: $t('stream_type')
					"
					chips
					autofocus-filter
					:disable="!manageLiveClasses"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-input
					v-if="platform_type !== 'jitsi'"
					v-model="embed"
					ref="embed"
					:float-label="$t('transmission_url')"
					:error="$v.embed.$error"
					@input="$v.embed.$touch"
					:disable="!manageLiveClasses"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="selected_client_params.live_class_use_score">
				<q-input
					v-model="score"
					:float-label="$t('score')"
					type="number"
					v-if="!selected_client_params.live_class_use_static_score && canSetScore"
					:disable="!manageLiveClasses"
				/>

				<q-select
					v-model="score"
					:options="scoresOptions"
					:float-label="$t('score')"
					v-if="selected_client_params.live_class_use_static_score && canSetScore"
					:disable="!manageLiveClasses"
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="isChevrolet">
				<q-field :helper="$t('training_code_helper_message')">
					<q-input
						type="text"
						v-model="code"
						:float-label="$t('training_code')"
						:disable="!manageLiveClasses"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="timezone"
					:options="timezones"
					:float-label="$t('timezone')"
					@input="changeTimezone"
					filter
					:disable="!manageLiveClasses"
				/>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-field
					:helper="`${$t('the_time_showed_here_will_be_adjust_for_the_user_timezone')}`"
				>
					<q-datetime
						v-model="date_class"
						ref="date_class"
						type="datetime"
						:float-label="$t('live_class_date')"
						format="DD/MM/YYYY HH:mm"
						:error="$v.date_class.$error"
						@input="$v.date_class.$touch"
						:disable="!manageLiveClasses"
					/>
				</q-field>
			</div>

			<div class="col-xs-6 col-sm-6">
				<q-input
					type="number"
					v-model="workload"
					ref="workload"
					:float-label="$t('workload_in_hours')"
					:error="$v.workload.$error"
					@input="$v.workload.$touch"
					:disable="!manageLiveClasses"
				/>
			</div>

			<div class="col-xs-11 col-sm-11">
				<q-select
					v-model="is_public"
					ref="is_public"
					:options="is_public_options"
					:float-label="$t('live_class_type')"
					:error="$v.is_public.$error"
					@input="$v.is_public.$touch"
					:disable="!manageLiveClasses"
				/>
			</div>

			<div style="padding: 10px 2px;" class="col-xs-1 col-sm-1">
				<q-icon
					name="fal fa-question-circle"
					size="1.5rem"
					color="grey"
					class="q-mr-sm q-pt-md"
				>
					<q-tooltip class="custom-tooltip" anchor="top left" self="bottom right">
						{{ getClassTypeTooltip(is_public) }}
					</q-tooltip>
				</q-icon>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-field>
					<q-input
						type="number"
						v-model.number="qty_limit_participants"
						ref="qty_limit_participants"
						filled
						:float-label="$t('qty_participants_info')"
						:error="$v.qty_limit_participants.$error"
						@input="$v.qty_limit_participants.$touch"
						:disable="!manageLiveClasses"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="tag"
					:options="optionTags"
					:float-label="$t('tags')"
					multiple
					chips
					filter
					autofocus-filter
					:disable="!manageLiveClasses"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('image') }}</p>
						<uploader
							content_type="images"
							@fileUploaded="imageUploaded"
							:multiple="false"
							:cropper="true"
							:aspect_ratio="52 / 29"
							:aspect_ratio_string="'52/29'"
							:disable="!manageLiveClasses"
						/>

						<p v-if="image_url" class="q-mt-md file-url">
							<q-icon name="fal fa-check" color="positive" />
							<a :href="image_url" target="_blank"> {{ $truncateLink(image_url) }}</a>
						</p>
					</div>
					<div class="col-xs-12 col-sm-6">
						<p class="caption">
							{{ $t('alt_image') }}
							<q-icon
								name="fal fa-question-circle"
								size="1.5rem"
								color="grey"
								class="q-ml-sm q-pb-sm"
							>
								<q-tooltip
									class="custom-tooltip"
									anchor="top left"
									self="bottom right"
								>
									{{ $t('alt_image_information_helper') }}
								</q-tooltip>
							</q-icon>
						</p>
						<div class="flex justify-between">
							<template v-for="lang in languages" class="row flex">
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'pt-br'">
									<q-input
										v-model="language_br"
										:float-label="$t(`${lang.name}`)"
										ref="language_br"
										:error="$v.language_br.$error"
										@input="$v.title.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'en-us'">
									<q-input
										v-model="language_us"
										:float-label="$t(`${lang.name}`)"
										ref="language_us"
										:error="$v.language_us.$error"
										@input="$v.title.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'es-es'">
									<q-input
										v-model="language_es"
										:float-label="$t(`${lang.name}`)"
										ref="language_es"
										:error="$v.language_es.$error"
										@input="$v.title.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'fr-fr'">
									<q-input
										v-model="language_fr"
										:float-label="$t(`${lang.name}`)"
										ref="language_fr"
										:error="$v.language_fr.$error"
										@input="$v.title.$touch"
									/>
								</q-field>
							</template>
						</div>
					</div>
				</div>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-field :helper="`${$t('date_to_unpublish')}`">
					<q-datetime
						v-model="unpublish_date"
						ref="unpublish_date"
						type="date"
						:float-label="$t('unpublish_date')"
						format="DD/MM/YYYY"
						clearable
						:disable="!manageLiveClasses"
					/>
				</q-field>
			</div>
		</div>

		<q-tabs v-model="tab" color="secondary" class="shadow-1 q-mb-md">
			<q-tab
				slot="title"
				name="permissions"
				icon="fal fa-clipboard-list-check"
				:label="$t('permissions')"
			/>

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('areas') }}</p>
						<q-checkbox
							class="q-mb-sm"
							:disable="!manageLiveClasses"
							v-model="all_areas"
							:val="true"
							:label="$t('select_all_areas')"
							@input="checkAllAreas()"
						/>
						<select-multiple
							label=""
							:filters="area"
							:options="areas"
							@on_value="area = $event"
							:error="$v.area.$error"
							:input="$v.area.$touch"
							:disable="!manageLiveClasses"
						/>
					</div>

					<div class="col-xs-12 col-sm-12" ref="profile">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									v-model="all_profiles"
									:val="true"
									:label="$t('all')"
									@input="checkAll()"
									:disable="!manageLiveClasses"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in profiles"
								:key="index"
								:class="getErrorClass($v.profile.$error)"
							>
								<q-checkbox
									keep-color
									v-model="profile"
									:val="item.value"
									:label="item.label"
									@input="$v.profile.$touch"
									:color="getErrorColor($v.profile.$error)"
									:disable="!manageLiveClasses"
								/>
							</div>
						</div>
					</div>
				</div>
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<q-select
							v-model="user_group"
							ref="user_groups"
							:options="user_groups"
							:float-label="$t('groups')"
							:error="$v.user_group.$error"
							@input="$v.user_group.$touch"
							multiple
							chips
							:disable="!manageLiveClasses"
						/>
					</div>
				</div>

				<div class="row gutter-sm q-my-sm" v-if="id_live_class != 0">
					<div class="col-xs-12 col-sm-11">
						<q-input
							v-model="url_live_class_portal"
							:float-label="$t('link')"
							:disable="true"
						/>
					</div>

					<div class="col-xs-12 col-sm-1 q-mt-lg">
						<q-btn
							dense
							color="primary"
							icon="fal fa-copy"
							class="full-width"
							@click="copyLink()"
						>
							<q-tooltip>
								{{ $t('copy_link') }}
							</q-tooltip>
						</q-btn>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<div class="col-xs-12 col-sm-12 q-mt-xs">
			<q-chips-input
				v-model="owners_list"
				ref="owners_list"
				:error="$v.owners.$error"
				@input="$v.owners.$touch"
				:float-label="$t('owners')"
				@remove="removeOwner"
				:disable="!manageLiveClasses"
			>
				<q-autocomplete
					@search="searchOwner"
					@selected="selectOwner"
					:min-characters="3"
					:value-field="(v) => `${v.full_name} - ${v.email}`"
				/>
			</q-chips-input>
		</div>

		<div class="row gutter-sm q-mt-sm">
			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('status') }}</p>
				<q-btn-toggle
					v-model="status"
					:toggle-color="getColorStatus"
					:options="statuses"
					:disable="!manageLiveClasses"
				/>
				<q-icon name="fal fa-question-circle" size="1.5rem" color="grey" class=" q-ml-md">
					<q-tooltip class="custom-tooltip" anchor="top left" self="bottom left">
						{{ $t('status_term') }}
					</q-tooltip>
				</q-icon>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'admin_live_classes' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" v-if="showDuplicateButton">
				<q-btn
					color="info"
					class="full-width"
					:label="$t('duplicate_live_class')"
					icon="far fa-copy"
					@click="show_confirm_duplicate = true"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
					:disable="!manageLiveClasses"
				/>
			</div>
		</div>
		<q-dialog v-model="show_confirm_duplicate" @ok="duplicateLiveClass">
			<span slot="title">{{ $t('duplicate_live_class') }}</span>

			<span slot="message">{{
				$t('confirm_duplicate_the_live_class_and_all_its_contents')
			}}</span>

			<template slot="buttons" slot-scope="props">
				<q-btn
					color="tertiary"
					:label="$t('cancel')"
					@click="show_confirm_duplicate = false"
				/>
				<q-btn color="positive" :label="$t('confirm')" @click="duplicateLiveClass()" />
			</template>
		</q-dialog>
	</div>
</template>

<script>
import Uploader from 'components/Uploader'
import { mapGetters } from 'vuex'
import {
	required,
	requiredIf,
	maxValue,
	minValue,
	minLength,
	maxLength,
} from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate, addToDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import SelectMultiple from 'components/utils/SelectMultiple'

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'training', 'admin', 'live_classes'] },

	props: ['manageLiveClasses'],

	data() {
		return {
			language_br: '',
			language_us: '',
			language_es: '',
			language_fr: '',
			all_profiles: false,
			area: [],
			code: null,
			description: '',
			id_live_class: '',
			image_url: '',
			date_class: null,
			live_class_room: '',
			is_public: true,
			is_public_options: [
				{
					label: this.$t('open_live_class_info'),
					value: true,
				},
				{
					label: this.$t('closed_live_class_info'),
					value: false,
				},
			],
			platform_type: 'jitsi',
			embed: null,
			owners: [],
			owners_list: [],
			profile: [],
			qty_limit_participants: 0,
			score: 0,
			all_areas: false,
			selected_client_params: [],
			status: 1,
			statuses: [
				{
					label: this.$t('published'),
					value: 1,
				},
				{
					label: this.$t('unpublished'),
					value: 2,
				},
				{
					label: this.$t('deleted'),
					value: 3,
				},
				{
					label: this.$t('finished'),
					value: 4,
				},
			],
			tab: 'permissions',
			tag: [],
			timezone: '',
			title: '',
			show_confirm_duplicate: false,
			user_group: [],
			workload: 0,
			unpublish_date: null,
		}
	},

	components: {
		uploader: Uploader,
		'select-multiple': SelectMultiple,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			timezones: 'registration_lists/timezones',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			tags: 'registration_lists/tags',
			scores: 'registration_lists/live_class_scores',
			live_class_platform_types: 'registration_lists/live_class_platform_types',
			user_module_items: 'common/user_module_items',
			user_id: 'common/id',
			user_full_name: 'common/full_name',
			user_email: 'common/email',
			languages: 'params/languages',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		optionTags() {
			if (this.tags.length > 0) {
				return this.tags.map((t) => {
					let areas = t.areas.map((a) => a.name)
					return {
						value: t.value,
						label: t.label,
						sublabel: areas.join(', '),
					}
				})
			} else return []
		},

		getColorStatus() {
			if (this.status == 1) return 'positive'
			else if (this.status == 2) return 'warning'
			else if (this.status == 4) return 'negative'
			else return 'negative'
		},

		canSetScore() {
			const obj = this.user_module_items.find((obj) => obj.slug === 'live_class_set_score')
			return obj ? obj.permission : false
		},

		getLiveClassPlatformTypes() {
			if (this.live_class_platform_types) {
				return this.live_class_platform_types.map((type) => {
					return {
						value: type.slug,
						label: type.label ? type.label : '',
					}
				})
			} else return []
		},

		isChevrolet() {
			return process.env.PROJECT_FOLDER === 'universidadechevrolet' ? true : false
		},

		url_live_class_portal() {
			const url = process.env.SITE_URL ? process.env.SITE_URL : document.location.origin
			return `${url}/live-classes/${this.id_live_class}`
		},

		showDuplicateButton() {
			return this.$route.params.id != 0 ? true : false
		},

		scoresOptions() {
			let scoreOpts = this.scores.map((e) => {
				return {
					value: e.score,
					label: e.label,
				}
			})
			let score = scoreOpts.find((e) => e.value == this.score)
			if (!score) {
				scoreOpts.push({
					value: this.score,
					label: this.score,
				})
			}
			return scoreOpts
		},
	},

	methods: {
		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_live_classes/live_classes/${this.id_live_class}/`, this.getHeader)
				.then((response) => {
					const d = response.data

					this.title = d.title
					this.description = d.description
					this.score = parseInt(d.score)
					this.image_url = d.image
					this.qty_limit_participants = d.qty_limit_participants
					this.is_public = d.is_public
					this.platform_type = d.platform_type
					this.embed = d.embed
					this.status = d.status
					this.date_class = moment(d.date_class)
						.tz(d.timezone)
						.format('YYYY-MM-DDTHH:mm:ss')
					this.tag = d.tags
					this.owners = d.owners
					if (d.list_owners && d.list_owners.length) {
						this.owners_list = []
						d.list_owners.forEach((owner) => {
							this.owners_list.push(`${owner.full_name} - ${owner.email}`)
						})
					}
					this.timezone = d.timezone
					this.live_class_room = d.code_room
					this.area = d.areas
					this.profile = d.profiles
					this.user_group = d.user_groups
					this.code = d.code
					this.workload = d.workload

					if (d.live_class_image_alt_texts) {
						const altTextMap = {}
						d.live_class_image_alt_texts.forEach((altTextObj) => {
							altTextMap[altTextObj.language] = altTextObj.alt_text
						})
						this.language_br = altTextMap['pt-br'] || ''
						this.language_us = altTextMap['en-us'] || ''
						this.language_es = altTextMap['es-es'] || ''
						this.language_fr = altTextMap['fr-fr'] || ''
					}

					this.unpublish_date = d.unpublish_date
						? date.formatDate(d.unpublish_date, 'YYYY-MM-DDT00:00:00.SSSZ')
						: null
					if (this.unpublish_date)
						this.unpublish_date = addToDate(this.unpublish_date, { days: 1 })

					this.changeTimezone(d.timezone)

					const classData = {
						id: this.id_live_class,
						title: this.title,
						description: this.description,
						is_public: this.is_public,
						room: this.live_class_room,
						guest_limit: this.qty_limit_participants,
						allowed_guest_areas: this.area,
						allowed_guest_profiles: this.profile,
						platform_type: this.platform_type,
						embed: this.embed,
						date_class: moment(date.formatDate(this.date_class, 'YYYY-MM-DDTHH:mm'))
							.tz(this.timezone, true)
							.format('YYYY-MM-DDTHH:mm:ssZ'),
						timezone: this.timezone,
						status: this.status,
					}

					const altTextsByLanguage = {
						'pt-br': 'language_br',
						'en-us': 'language_us',
						'es-es': 'language_es',
						'fr-fr': 'language_fr',
					}

					response.data.live_class_image_alt_texts.forEach((l) => {
						const altTextProp = altTextsByLanguage[l.language]
						if (altTextProp) {
							this[altTextProp] = l.alt_text
						}
					})

					this.$emit('updateClassData', classData)
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				const data = {
					title: this.title,
					description: this.description,
					platform_type: this.platform_type,
					embed:
						this.embed === null
							? this.embed
							: this.embed.includes('http')
							? this.embed
							: `https://${this.embed}`,
					score: this.score
						? !isNaN(this.score)
							? this.score
							: parseInt(this.score)
						: 0,
					date_class: moment(date.formatDate(this.date_class, 'YYYY-MM-DDTHH:mm'))
						.tz(this.timezone, true)
						.format('YYYY-MM-DDTHH:mm:ssZ'),
					timezone: this.timezone,
					owners: this.owners,
					is_public: this.is_public,
					qty_limit_participants: this.qty_limit_participants,
					status: this.status,
					image:
						this.image_url && this.image_url.split('?')[0].length
							? this.image_url.split('?')[0]
							: this.image_url,
					code: this.code,
					workload: this.workload,
					live_class_image_alt_texts: {
						'pt-br': this.language_br ? this.language_br : '',
						'en-us': this.language_us ? this.language_us : '',
						'es-es': this.language_es ? this.language_es : '',
						'fr-fr': this.language_fr ? this.language_fr : '',
					},
				}

				if (this.unpublish_date)
					data.unpublish_date = date.formatDate(this.unpublish_date, 'YYYY-MM-DD')
				if (this.tag.length) data.tags = this.tag
				if (this.area.length > 0) data.areas = this.area
				if (this.profile.length > 0) data.profiles = this.profile
				if (this.user_group.length > 0) data.user_groups = this.user_group

				const classData = {
					id: this.id_live_class,
					title: this.title,
					description: this.description,
					is_public: this.is_public,
					room: this.live_class_room,
					guest_limit: this.qty_limit_participants,
					allowed_guest_areas: this.area,
					allowed_guest_profiles: this.profile,
					platform_type: this.platform_type,
					embed: this.embed,
					date_class: moment(date.formatDate(this.date_class, 'YYYY-MM-DDTHH:mm'))
						.tz(this.timezone, true)
						.format('YYYY-MM-DDTHH:mm:ssZ'),
					timezone: this.timezone,
					status: this.status,
				}

				if (this.id_live_class === 0) {
					this.$axios
						.post(`/admin_live_classes/live_classes/`, data, this.getHeader)
						.then(({ data }) => {
							this.id_live_class = data.id
							this.$router.replace({ params: { id: data.id } })
							this.status = data.status

							this.$emit('updateClassData', classData)

							this.$q.notify({
								message: this.$t('live_class_save_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})

							this.owners = []
							this.owners_list = []
						})
						.catch((e) => {
							console.log(e)

							this.$q.notify({
								message: this.$t('live_class_save_error'),
								type: 'negative',
								icon: 'fal fa-ban',
							})
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				} else {
					this.$axios
						.put(
							`/admin_live_classes/live_classes/${this.id_live_class}/`,
							data,
							this.getHeader
						)
						.then(({ data }) => {
							this.status = data.status

							this.$emit('updateClassData', classData)

							this.$q.notify({
								message: this.$t('live_class_save_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})
						})
						.catch((e) => {
							console.log(e)

							this.$q.notify({
								message: this.$t('live_class_save_error'),
								type: 'negative',
								icon: 'fal fa-ban',
							})
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fal fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		imageUploaded(url) {
			this.image_url = url
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles.forEach((element) => {
					this.profile.push(element.value)
				})
			} else {
				this.profile = []
			}
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.area = this.areas.map((e) => e.value)
			} else {
				this.area = []
			}
		},

		getErrorColor(state) {
			return state ? 'negative' : ''
		},

		getErrorClass(state) {
			return state ? 'checkbox-error' : ''
		},

		getOrder(name) {
			if (name == 'cancel') {
				return this.$q.platform.is.mobile ? 'order-last' : 'order-first'
			} else if (name == 'save') {
				return this.$q.platform.is.mobile ? 'order-first' : 'order-last'
			}
		},

		getProfile(id) {
			const obj = this.profiles.find((obj) => obj.value === id)
			return obj ? obj.label : ''
		},

		searchOwner(terms, done) {
			this.$axios
				.get(`/accounts/get_owners/?search=${terms}`, this.getHeader)
				.then(({ data }) => {
					let users = []

					data.forEach((element) => {
						users.push({
							value: element.id,
							label: `${element.full_name} - ${element.email}`,
							full_name: element.full_name,
							email: element.email,
							id: element.id,
						})
					})

					done(users)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		selectOwner(item, keyboard) {
			if (!keyboard) this.owners.push(item.id)
		},

		removeOwner({ index, value }) {
			this.owners.splice(index, 1)
		},

		getClassTypeTooltip(is_public) {
			switch (is_public) {
				case false:
					return this.$t('live_classes_rules_tip_open_class')
				case true:
					return this.$t('live_classes_rules_tip_closed_class')
				default:
					return this.$t('live_classes_rules_select_class_type')
			}
		},

		getCurrentTimezone() {
			return date.formatDate(new Date(), 'Z')
		},

		fetchTimezones() {
			let values = []

			moment.tz
				.names()
				.reduce((memo, tz) => {
					memo.push({
						name: tz,
						offset: moment.tz(tz).utcOffset(),
					})

					return memo
				}, [])
				.sort((a, b) => {
					return a.offset - b.offset
				})
				.reduce((memo, tz) => {
					const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
					values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
				}, '')

			this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
		},

		changeTimezone(timezone) {
			this.timezone = timezone
		},

		copyLink() {
			this.$copyText(this.url_live_class_portal).then(
				(e) => {
					this.$q.notify({
						message: this.$t('link_copied_to_clipboard'),
						type: 'positive',
						icon: 'fal fa-copy',
					})
				},
				(e) => {
					console.log(e)
				}
			)
		},

		duplicateLiveClass() {
			this.show_confirm_duplicate = false
			this.$q.loading.show()

			this.$axios
				.get(
					`/admin_live_classes/live_classes/${this.id_live_class}/duplicate/`,
					this.getHeader
				)
				.then(({ data }) => {
					this.$router.push({
						name: 'admin_live_class',
						params: { id: data.live_class_duplicated_id },
					})

					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},
	},

	validations() {
		return {
			title: { required, maxLength: maxLength(256) },
			language_br: { maxLength: maxLength(170) },
			language_us: { maxLength: maxLength(170) },
			language_es: { maxLength: maxLength(170) },
			language_fr: { maxLength: maxLength(170) },
			description: { required, maxLength: maxLength(1024) },
			is_public: { required },
			workload: { required },
			qty_limit_participants: { required, minValue: minValue(1), maxValue: maxValue(1000) },
			date_class: { required },
			owners: { required },
			platform_type: { required },
			// unpublish_date: { minValue: (value) => value > new Date().toISOString() },
			embed: {
				requiredIf: requiredIf(() => {
					return this.platform_type !== 'jitsi' ? true : false
				}),
			},
			area: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						return this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
							? true
							: false
					} else {
						return true
					}
				}),
			},
			profile: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						return this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
							? true
							: false
					} else {
						return true
					}
				}),
			},
			user_group: {
				requiredIf: requiredIf(() => {
					return this.profile.length == 0 &&
						this.area.length == 0 &&
						this.user_group.length == 0
						? true
						: false
				}),
			},
		}
	},

	created() {
		this.fetchTimezones()
		this.id_live_class = this.$route.params.id

		if (this.id_live_class !== 0) {
			this.fetchData()
		} else {
			this.timezone = moment.tz.guess()
			this.changeTimezone(this.timezone)

			if (this.owners.length <= 0) {
				this.owners.push(this.user_id)
				this.owners_list.push(`${this.user_full_name} - ${this.user_email}`)
			}
		}

		const client = this.clients.find((obj) => obj.id === this.id_user_client_selected)
		this.selected_client_params = client ? client : {}
	},

	watch: {
		$route: function(refreshPage) {
			this.fetchTimezones()
			this.id_live_class = this.$route.params.id

			if (this.id_live_class !== 0) {
				this.owners = []
				this.owners_list = []
				this.fetchData()
			} else {
				this.timezone = moment.tz.guess()
				this.changeTimezone()
			}
		},
	},
}
</script>

<style lang="stylus" scoped>
@import '~variables'

.custom-tooltip
    width 450px
    font-size 0.9rem
    padding 0.8rem

.file-url
    a
        text-decoration none
        color black
</style>
